import React, { useRef, useState } from 'react';
import { Download, FileText, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useT } from '../../lib/useT';

export function LegalBidWorkbench({ onSwitchView: _onSwitchView }: { onSwitchView?: (view: any) => void }) {
  const t = useT();
  const isZh = t.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const [requirements, setRequirements] = useState('');
  const [projectName, setProjectName] = useState('');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => formData.append('files', file));
      const res = await fetch('/api/files/upload', { method: 'POST', body: formData, credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || ui('文件上传解析失败', 'File upload parsing failed'));
      const text = (data.files || [])
        .map((file: any) => String(file.content || file.preview || '').trim())
        .filter(Boolean)
        .join('\n\n');
      if (!text) throw new Error(ui('没有提取到可生成标书的文本', 'No bid requirements text extracted'));
      setRequirements(prev => [prev, text].filter(Boolean).join('\n\n'));
      toast.success(ui('文件已解析并填入招标要求', 'File parsed into bid requirements'));
    } catch (err: any) {
      const message = err?.message || ui('文件上传解析失败', 'File upload parsing failed');
      setResult(`${ui('错误', 'Error')}: ${message}`);
      toast.error(message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const generateBid = async () => {
    if (!requirements.trim() || loading) return;
    setLoading(true);
    setResult('');
    try {
      const msg = projectName
        ? `请使用 legal_generate_bid 工具为项目“${projectName}”生成标书：\n\n${requirements}`
        : `请使用 legal_generate_bid 工具根据以下招标要求生成标书：\n\n${requirements}`;
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, stream: false }),
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || ui('标书生成失败', 'Bid generation failed'));
      setResult(data.text || data.response || data.reply || data.message || JSON.stringify(data));
    } catch (e: any) {
      setResult(`${ui('错误', 'Error')}: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const exportBid = () => {
    if (!result) return;
    const blob = new Blob([result], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${projectName || '投标提案'}.md`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full overflow-y-auto p-6 text-white">
      <div className="mx-auto flex max-w-6xl flex-col gap-4">
        <section className="rounded-lg border border-white/10 bg-white/[0.04] p-5">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-violet-400/20 bg-violet-500/10 text-violet-300">
              <FileText size={22} />
            </span>
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-white">{t.legalBidGenTitle || ui('标书生成', 'Bid Proposal Workbench')}</h2>
              <p className="mt-1 text-sm leading-6 text-white/50">
                {t.legalBidGenDesc || ui('解析招标要求并生成可编辑的商务标、技术标框架。', 'Parse tender requirements and generate an editable commercial/technical proposal framework.')}
              </p>
            </div>
          </div>
        </section>

        <section className="grid min-h-[560px] gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="flex min-h-0 flex-col rounded-lg border border-white/10 bg-white/[0.04] p-4">
            <div className="mb-3 grid gap-3 md:grid-cols-[1fr_auto]">
              <input
                type="text"
                value={projectName}
                onChange={event => setProjectName(event.target.value)}
                placeholder={ui('项目名称（可选）', 'Project name (optional)')}
                className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-white outline-none placeholder:text-white/35 focus:border-violet-400/35"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading || loading}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/65 transition hover:bg-white/10 hover:text-white disabled:opacity-50"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {t.legalBidGenUpload || ui('上传文件', 'Upload')}
              </button>
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" onChange={handleFileUpload} className="hidden" />
            </div>
            <textarea
              value={requirements}
              onChange={event => setRequirements(event.target.value)}
              placeholder={t.legalBidGenPlaceholder || ui('粘贴招标文件、评分标准、技术要求、合同条款...', 'Paste tender document, scoring rules, technical requirements, and contract terms...')}
              className="min-h-[420px] flex-1 resize-none rounded-lg border border-white/10 bg-black/20 px-3 py-3 text-sm leading-6 text-white outline-none placeholder:text-white/35 focus:border-violet-400/35"
            />
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-white/40">{t.legalBidGenPaste || ui('可粘贴文本，也可上传 PDF/DOCX/TXT 文件。', 'Paste text or upload PDF/DOCX/TXT files.')}</p>
              <button
                onClick={generateBid}
                disabled={loading || !requirements.trim()}
                className="inline-flex items-center gap-2 rounded-lg border border-violet-400/20 bg-violet-500/15 px-4 py-2.5 text-sm font-medium text-violet-100 transition hover:bg-violet-500/25 disabled:opacity-50"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                {t.legalBidGenGenerate || ui('生成标书', 'Generate')}
              </button>
            </div>
          </div>

          <div className="flex min-h-0 flex-col rounded-lg border border-white/10 bg-white/[0.04] p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="text-sm font-medium text-white">{ui('输出', 'Output')}</h3>
              {result && (
                <button
                  onClick={exportBid}
                  className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/65 transition hover:bg-white/10 hover:text-white"
                >
                  <Download size={14} />
                  {t.legalBidGenExport || ui('导出', 'Export')}
                </button>
              )}
            </div>
            <div className="min-h-[420px] flex-1 overflow-y-auto rounded-lg border border-white/10 bg-black/20 p-4 custom-scrollbar">
              {result ? (
                <article className="whitespace-pre-wrap text-sm leading-7 text-white/78">{result}</article>
              ) : (
                <div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-2 text-center text-sm text-white/40">
                  <FileText size={32} className="text-white/20" />
                  <span>{ui('生成的标书内容会显示在这里。', 'Generated bid proposal will appear here.')}</span>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
